import { useMemo } from 'react';
import { ChainIds } from '@sovryn/ethers-provider';
import { toDisplayPrice } from '@sovryn/sdex';
import { Decimal } from '@sovryn/utils';
import { useTokenPrices } from '../contexts/TokenPricesContext';
import { COMMON_SYMBOLS } from '../utils/asset';
import { isRskChain } from '../utils/chain';
import { fromWei } from '../utils/math';
import { useCurrentChain } from './useChainStore';
import { useTokenDetailsByAsset } from './useTokenDetailsByAsset';
const STABLECOINS = {
    [ChainIds.MAINNET]: ['USDT', 'USDC', 'DAI'],
    [ChainIds.BOB_MAINNET]: ['DLLR', 'USDT', 'USDC', 'DAI'],
    [ChainIds.BOB_TESTNET]: ['DLLR', 'USDT', 'USDC', 'DAI'],
    [ChainIds.RSK_MAINNET]: ['DLLR', 'XUSD', 'ZUSD', 'DOC', 'RUSDT'],
    [ChainIds.RSK_TESTNET]: ['DLLR', 'XUSD', 'ZUSD', 'DOC', 'RUSDT'],
};
export function useDollarValue(asset, weiAmount, chainId) {
    const currentChainId = useCurrentChain();
    const chain = chainId || currentChainId;
    const { prices, loading: pricesLoading } = useTokenPrices();
    const destination = useMemo(() => { var _a, _b; return ((_b = (_a = STABLECOINS[chain]) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : COMMON_SYMBOLS.DLLR).toUpperCase(); }, [chain]);
    const entry = useMemo(() => {
        if (asset.toUpperCase() === COMMON_SYMBOLS.OSSOV) {
            return COMMON_SYMBOLS.SOV;
        }
        if (isRskChain(chain)) {
            if (asset.toUpperCase() === COMMON_SYMBOLS.ZUSD) {
                return COMMON_SYMBOLS.XUSD;
            }
            else if (asset.toLocaleLowerCase() === 'weth') {
                return COMMON_SYMBOLS.ETH;
            }
            else if (['wbtc', 'tbtc'].includes(asset.toLocaleLowerCase())) {
                return COMMON_SYMBOLS.BTC;
            }
            else if (asset.toLocaleLowerCase() === 'esov') {
                return COMMON_SYMBOLS.SOV;
            }
        }
        return asset.toUpperCase();
    }, [asset, chain]);
    const assetDetails = useTokenDetailsByAsset(entry, chain);
    const destinationDetails = useTokenDetailsByAsset(destination, chain);
    const usdPrice = useMemo(() => {
        if (assetDetails && assetDetails.address) {
            return prices[assetDetails.address.toLowerCase()] || '0';
        }
        return '0';
    }, [prices, assetDetails]);
    const usdValue = useMemo(() => {
        var _a, _b;
        if (entry === destination ||
            (((_a = STABLECOINS[chain]) === null || _a === void 0 ? void 0 : _a.includes(entry)) &&
                ((_b = STABLECOINS[chain]) === null || _b === void 0 ? void 0 : _b.includes(destination)))) {
            const amount = Number(fromWei(weiAmount, (assetDetails === null || assetDetails === void 0 ? void 0 : assetDetails.decimals) || 18));
            return toDisplayPrice(amount, (destinationDetails === null || destinationDetails === void 0 ? void 0 : destinationDetails.decimals) || 18, (assetDetails === null || assetDetails === void 0 ? void 0 : assetDetails.decimals) || 18).toString();
        }
        else {
            const amount = Decimal.fromBigNumberString(weiAmount)
                .mul(usdPrice)
                .toString();
            return amount;
        }
    }, [
        entry,
        destination,
        chain,
        weiAmount,
        assetDetails === null || assetDetails === void 0 ? void 0 : assetDetails.decimals,
        destinationDetails === null || destinationDetails === void 0 ? void 0 : destinationDetails.decimals,
        usdPrice,
    ]);
    return {
        loading: pricesLoading,
        usdValue,
        usdPrice,
    };
}
